import { Component, DestroyRef, inject, NgZone, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SearchInputComponent } from '../../../../../../components/inputs/search-input/search-input.component';
import {
  StatusFilterComponent,
  StatusFilterOption,
} from '../../../../../../components/inputs/status-filter/status-filter.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../../../../components/inputs/sort-dropdown/sort-dropdown.component';
import { FeedbackCardComponent } from '../../../../../../components/toasts-and-cards/feedback-card/feedback-card.component';
import { PaginationComponent } from '../../../../../../components/inputs/pagination/pagination.component';
import { MessageService } from '../../../../../../services/message/message.service';
import { LiveRefresh } from '../../../../../../services/live/live-refresh';
import { PromptCardComponent } from '../../../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { IPagination } from '../../../../../../types/contactModule';
import { EmptyStateComponent } from '../../../../../../components/ui/empty-state/empty-state.component';
import { StatusComponent } from '../../../../../../components/ui/status/status.component';
import { DataTableComponent } from '../../../../../../components/ui/data-table/data-table.component';
import { SmsTextComponent } from '../../../../../../components/messages/sms-text/sms-text.component';
import { DeliveryComponent } from '../../../../../../components/messages/delivery/delivery.component';
import { MessageRow, toMessageRows } from '../active-messages-table/message-row';

type TStatusSort = 'all' | 'canceled' | 'draft';
type TArrangementSort =
  | 'messageSubject,asc'
  | 'messageSubject,desc'
  | 'createdAt,desc'
  | 'createdAt,asc'
  | 'modifiedAt,desc'
  | 'modifiedAt,asc';

/** Drafts and cancelled sends change only when someone edits them; a minute is plenty. */
const LIVE_MS = 60_000;

@Component({
  selector: 'inactive-messages-table',
  standalone: true,
  imports: [
    SearchInputComponent,
    MatTooltipModule,
    PromptCardComponent,
    StatusFilterComponent,
    SortDropdownComponent,
    FeedbackCardComponent,
    PaginationComponent,
    EmptyStateComponent,
    StatusComponent,
    DataTableComponent,
    SmsTextComponent,
    DeliveryComponent,
    RouterLink,
  ],
  templateUrl: './inactive-messages-table.component.html',
  styleUrls: [
    '../active-messages-table/message-table.css',
    './inactive-messages-table.component.css',
  ],
})
export class InactiveMessagesTableComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private zone = inject(NgZone);
  private searchSubject = new Subject<string>();

  router = inject(Router);
  messageService = inject(MessageService);

  messages: any[] = [];
  /** Display values for each message, worked out once per load. */
  rows: MessageRow[] = [];
  selectedMessage: any = {};
  showArchiveModal: boolean = false;
  showCancelModal: boolean = false;
  showDeleteModal: boolean = false;
  statusSort: TStatusSort = 'all';
  arrangementSort: TArrangementSort = 'createdAt,desc';

  readonly statusOptions: StatusFilterOption[] = [
    { value: 'all', title: 'All', icon: 'list', tone: 'neutral' },
    // Neutral, as app-status draws drafts and cancelled sends (idle grey).
    { value: 'draft', title: 'Draft', icon: 'edit_note', tone: 'neutral' },
    {
      value: 'canceled',
      title: 'Cancelled',
      icon: 'cancel',
      tone: 'neutral',
    },
  ];

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'messageSubject',
    nameLabel: 'Subject',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });
  messagePage: number = 0;
  pageSize: number = 10;
  searchTerm: string = '';
  paginationData: IPagination = {
    totalElements: 0,
    pageSize: this.pageSize,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  };
  loading: boolean = false;

  private params: Record<string, any> = {
    'archive.equals': false,
    'status.in': ['Draft', 'Canceled'],
    sort: 'createdAt,desc',
  };

  get emptyMessage(): string {
    if (this.searchTerm.trim()) return `No messages match “${this.searchTerm.trim()}”.`;
    if (this.statusSort === 'draft') return 'No drafts.';
    if (this.statusSort === 'canceled') return 'No cancelled sends.';
    return 'No drafts or cancelled sends.';
  }

  ngOnInit(): void {
    this.messageService.getInactiveMessages
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.messages = data ?? [];
        this.rows = toMessageRows(this.messages);
      });

    this.messageService.getInActiveMessagesPagination
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: IPagination) => {
        this.paginationData = value;
      });

    this.messageService.getLoadingInActiveMessages
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => (this.loading = loading));

    this.searchSubject
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.fetchMessages());

    this.fetchMessages();

    const live = new LiveRefresh(this.zone, {
      refresh: () => this.fetchMessages(true),
      interval: () => LIVE_MS,
    });
    live.start();
    this.destroyRef.onDestroy(() => live.stop());
  }

  private fetchMessages(silent = false): void {
    // Spring Pageable reads `size`; `pageSize` is ignored.
    const params: Record<string, any> = { ...this.params, page: this.messagePage, size: this.pageSize };
    if (this.searchTerm) {
      params['messageSubject.contains'] = this.searchTerm;
    }
    this.messageService.fetchInactiveMessages(params, { silent });
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.messagePage = 0;
    this.fetchMessages();
  }


  openArchiveModal(selectedMessage: any) {
    this.selectedMessage = selectedMessage;
    this.showArchiveModal = true;
  }

  closeArchiveModal() {
    this.showArchiveModal = false;
  }

  handleArchiveMessage() {}

  openCancelModal(selectedMessage: any) {
    this.selectedMessage = selectedMessage;
    this.showCancelModal = true;
  }

  closeCancelModal() {
    this.showCancelModal = false;
  }

  handleCancelMessage() {
    if (this.selectedMessage?.uuid) {
      this.messageService
        .updateMessageStatus({
          id: this.selectedMessage.uuid,
          uuid: this.selectedMessage.uuid,
          status: 'Canceled',
        })
        .subscribe({
          next: () => {
            this.fetchMessages();
            this.closeCancelModal();
          },
          error: (err) => {
            this.closeCancelModal();
          },
        });
    }
  }

  openDeleteMessagePrompt(message: any) {
    this.selectedMessage = message;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
  }

  handleDeleteMessage() {
    if (this.selectedMessage?.uuid) {
      this.messageService.deleteMessage(this.selectedMessage.uuid).subscribe({
        next: () => {
          this.fetchMessages();
          this.closeDeleteModal();
        },
        error: (err) => {
          this.closeDeleteModal();
        },
      });
    }
  }

  updateMessagesPage(newPage: number) {
    this.messagePage = newPage;
    this.fetchMessages();
  }

  updateSortCriteria(criteria: TStatusSort) {
    this.statusSort = criteria;
    this.params['status.in'] =
      criteria === 'all'
        ? ['Draft', 'Canceled']
        : [criteria === 'draft' ? 'Draft' : 'Canceled'];
    this.messagePage = 0;
    this.fetchMessages();
  }

  updateSortArrangementCriteria(criteria: TArrangementSort) {
    this.arrangementSort = criteria;
    this.params['sort'] = criteria;
    this.fetchMessages();
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term;
    this.messagePage = 0;
    this.searchSubject.next(term);
  }

  goToMessageDetails(message: any) {
    // The details page loads by uuid (as the active and archived tables link).
    this.messageService.updateSelectedMessage(message);
    this.router.navigate(['/organization/messages', message?.uuid]);
  }

  /**
   * The subject is the row's link: it navigates itself (and opens a new tab
   * on Cmd/Ctrl-click), so the row's own click handler stays out of it.
   */
  selectFromLink(event: Event, message: any) {
    event.stopPropagation();
    this.messageService.updateSelectedMessage(message);
  }

  editMessage(message: any) {
    if (message?.status?.toLowerCase() !== 'draft') {
      return;
    }
    this.router.navigate(['/organization/messages/new'], {
      queryParams: message,
    });
  }

  goToMessageReport(message: any) {
    this.messageService.updateSelectedMessage(message);
    this.router.navigate(['/organization/messages/report', message?.uuid]);
  }
}
